import { useCallback, useEffect, useState } from 'react'

import { discoveryApi } from '../api'
import { savedToActivity, type Activity } from '../types'

export type ActivityListKind = 'agenda' | 'history' | 'saved'
export type ActivityListPhase = 'loading' | 'ready' | 'error'

export interface ActivityListState {
  items: Activity[]
  phase: ActivityListPhase
  refreshing: boolean
  error: unknown
  refresh: () => void
  setItems: (updater: (items: Activity[]) => Activity[]) => void
}

async function loadActivities(
  kind: ActivityListKind,
  signal: AbortSignal,
): Promise<Activity[]> {
  if (kind === 'agenda') {
    const response = await discoveryApi.listAgenda(signal)
    return response.items
  }
  if (kind === 'history') {
    const response = await discoveryApi.listHistory(signal)
    return response.items
  }
  const response = await discoveryApi.listSavedActivities(signal)
  return response.items.map(savedToActivity)
}

/**
 * Shared explicit lifecycle for the agenda, bookmarks and history screens.
 * A manual refresh keeps the previous items on screen (`refreshing`) instead of
 * replacing them with the skeleton; the first load still uses `phase=loading`.
 */
export function useActivityList(kind: ActivityListKind): ActivityListState {
  const [items, setItemsState] = useState<Activity[]>([])
  const [phase, setPhase] = useState<ActivityListPhase>('loading')
  const [refreshing, setRefreshing] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [attempt, setAttempt] = useState(0)

  const setItems = useCallback((updater: (items: Activity[]) => Activity[]) => {
    setItemsState(updater)
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    loadActivities(kind, controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return
        setItemsState(next)
        setPhase('ready')
        setRefreshing(false)
        setLoaded(true)
        setError(null)
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        setError(cause)
        setPhase('error')
        setRefreshing(false)
      })
    return () => controller.abort()
  }, [attempt, kind])

  const refresh = useCallback(() => {
    setError(null)
    if (loaded) {
      setRefreshing(true)
    } else {
      setPhase('loading')
    }
    setAttempt((value) => value + 1)
  }, [loaded])

  return { items, phase, refreshing, error, refresh, setItems }
}
